/**
 * Weighted claims (docs/RULES.md): what the Claims switch reads.
 *
 * /api/weights answers, for the people this caller may see, each person-month's
 * Σ(weight − 1) under the active claim rules; `derive` adds it to billed claims when the
 * switch is on Weighted. Fetched once, in the background, after the bundle — the first
 * paint never waits on it, and Standard needs nothing from it.
 *
 * Each page opens on its own default (basisLabel.ts, basisDefaultFor): Standard, except
 * the Scorecard, which opens on Weighted. Nothing is remembered between pages or visits,
 * so nobody reads weighted figures without the switch saying so. It is not a filter, so
 * "Clear all filters" leaves it alone.
 */
import { createContext, createElement, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import { TOKEN_HEADER } from './client'
import { apiFetch } from '@/auth/apiFetch'
import { withViewAs } from './viewAs'
import { usePageLimit } from '@/access/pageLimit'

export type { ClaimBasis } from './basisLabel'

export type WeightsState =
  | { status: 'loading' }
  | {
      status: 'ok'
      /** The rule set's tag; changes whenever a rule does. */
      tag: string
      rules: { coinsurancePayers: number; hardPayers: number }
      adjustments: Record<string, Record<string, number>>
      /** Σ(weight − 1) per crosstab cell; see data/weightedCrosstab.ts. */
      cells: Record<string, number>
      /**
       * 'model': the pipeline weighed the claims (official, docs/RULES.md). 'preview':
       * the app did, from the rules, because the model does not yet.
       */
      source: 'model' | 'preview'
      /** When the model read the rules (ISO); null for a preview. */
      rulesVersion: string | null
      /** The rules changed after the model read them: the next refresh applies them. */
      stale: boolean
    }
  /** The rules or the claim rows could not be read: Standard only. */
  | { status: 'unavailable'; reason: string }


/**
 * Outside a WeightsProvider (a test rendering pages on their own) there are no weights,
 * and the switch stays on Standard.
 */
const Ctx = createContext<WeightsState>({ status: 'unavailable', reason: 'Weights are not loaded here.' })

export const useWeights = () => useContext(Ctx)

/** Fetches /api/weights once, after sign-in. In App.tsx, above AppDataProvider. */
export function WeightsProvider({ children }: { children: ReactNode }) {
  return createElement(Ctx.Provider, { value: useFetchWeights() }, children)
}

function useFetchWeights(): WeightsState {
  const { token, ready } = useAuth()
  // Not asked for a page-limited reader: none of their pages has the switch, and the
  // API refuses them (api/shared/pages.ts).
  const limited = usePageLimit() !== null
  const [state, setState] = useState<WeightsState>({ status: 'loading' })
  useEffect(() => {
    if (limited) {
      setState({ status: 'unavailable', reason: 'Weighted claims are not part of your access.' })
      return
    }
    if (!ready) return
    let cancelled = false
    const headers: Record<string, string> = withViewAs({ Accept: 'application/json' })
    if (token) headers[TOKEN_HEADER] = token
    apiFetch('/api/weights', { headers })
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 503 ? 'Claim rows are not available yet.' : `Weights failed (${r.status}).`)
        const body = (await r.json()) as {
          weights: string
          rules: { coinsurancePayers: number; hardPayers: number }
          adjustments: Record<string, Record<string, number>>
          cells?: Record<string, number>
          source?: 'model' | 'preview'
          rulesVersion?: string | null
          stale?: boolean
        }
        if (cancelled) return
        setState(
          body.weights === 'unavailable'
            ? { status: 'unavailable', reason: 'The claim rules could not be read.' }
            : {
                status: 'ok',
                tag: body.weights,
                rules: body.rules,
                adjustments: body.adjustments ?? {},
                cells: body.cells ?? {},
                // An API from before the model wrote weights only ever previewed.
                source: body.source === 'model' ? 'model' : 'preview',
                rulesVersion: body.rulesVersion ?? null,
                stale: body.stale === true,
              },
        )
      })
      .catch((e) => !cancelled && setState({ status: 'unavailable', reason: e instanceof Error ? e.message : String(e) }))
    return () => {
      cancelled = true
    }
  }, [token, ready, limited])
  return state
}
